/**
 * Survivor Fantasy League - Data Models & Schema
 * 
 * This file defines the data models and Firestore schema for the Survivor fantasy league application.
 * It includes all collections, sub-collections, and their field definitions as specified in the requirements.
 */

// ============================================================================
// TOP-LEVEL COLLECTIONS
// ============================================================================

/**
 * Roles Collection Schema
 * Each document represents a role in the system
 */
export const ROLE_SCHEMA = {
  // Document ID: role identifier (e.g., 'player', 'league_manager', 'admin')
  name: 'string', // Display name: 'Player', 'League Manager', 'Admin'
  description: 'string', // Description of what this role can do
  permissions: ['string'], // Array of permission strings
  level: 'number', // Hierarchy level (1=player, 2=league_manager, 3=admin)
  isActive: 'boolean', // Whether this role is currently active
  createdAt: 'timestamp'
};

/**
 * Users Collection Schema
 * Each document represents a user in the system
 */
export const USER_SCHEMA = {
  // Document ID: auto-generated by Firebase Auth
  email: 'string', // Used for login and identification
  displayName: 'string', // e.g., "Jane Doe"
  roles: ['string'], // Array of role IDs: ['player', 'league_manager', 'admin']
  teams: {
    // Nested map structure: leagueId -> seasonId -> teamId or { teamId, claimedAt }
    // Supports both old format (string teamId) and new format (object with teamId and claimedAt)
    // Example (old format): {'leagueId1': {'seasonId1': 'teamIdA', 'seasonId2': 'teamIdB'}}
    // Example (new format): {'leagueId1': {'seasonId1': {teamId: 'teamIdA', claimedAt: timestamp}}}
    // Multiple users can claim the same team (allowed)
    type: 'map',
    value: 'map'
  },
  managedLeagues: ['string'], // Array of league IDs that the user manages
  createdAt: 'timestamp',
  lastLogin: 'timestamp'
};

/** Canonical `weeks[].eliminationType` values (avoid string drift in UI and scoring). */
export const ELIMINATION_TYPES = {
  SINGLE: 'single',
  DOUBLE: 'double',
  /** Two boots same episode; ordered picks for predictions; survivors get two survival increments, boots zero. */
  DOUBLE_SIMULTANEOUS: 'double_simultaneous',
  TRIPLE: 'triple',
};

/**
 * Seasons Collection Schema
 * Each document represents a Survivor season
 */
export const SEASON_SCHEMA = {
  // Document ID: auto-generated or custom (e.g., "survivor-50")
  name: 'string', // e.g., "Survivor 50: Philippines"
  status: 'string', // 'active' or 'complete'
  contestants: ['string'], // Array of contestant IDs for that season
  mergeWeek: 'number', // Week number when the merge occurred (null if not yet merged)
  weeks: [{
    weekNumber: 'number',
    airDate: 'date', // Date when the episode airs
    isFinale: 'boolean',
    isMergeWeek: 'boolean', // Whether this is the week when the merge occurred
    isOpenForPredictions: 'boolean',
    eliminationType: 'string', // ELIMINATION_TYPES: single | double | double_simultaneous | triple
    results: {
      // Object for storing votedOut, winner, etc.
      votedOut: 'string', // contestantId
      winner: 'string', // contestantId (for finale)
      fireMakingLoser: 'string', // contestantId (for finale)
      immunityWinner: 'string', // contestantId
      rewardWinner: 'string' // contestantId
    },
    scoreCalculationHistory: [{
      // Array of score calculation records
      calculatedAt: 'timestamp', // When scores were calculated
      calculatedBy: 'string', // User ID who triggered the calculation (optional)
      scope: 'string' // 'week' or 'season' - whether this was a week-specific or season-wide calculation
    }]
  }],
  startDate: 'timestamp',
  endDate: 'timestamp',
  createdAt: 'timestamp'
};

/**
 * Contestants Collection Schema
 * Each document represents a Survivor contestant
 */
export const CONTESTANT_SCHEMA = {
  // Document ID: auto-generated or custom (e.g., "jeff-probst")
  name: 'string', // e.g., "Jeff Probst"
  bio: 'string', // Optional biography
  seasons: ['string'], // Array of season IDs to track which seasons they have played in
  imageUrl: 'string', // Optional profile image URL
  createdAt: 'timestamp'
};

/**
 * Leagues Collection Schema
 * Each document represents a fantasy league
 */
export const LEAGUE_SCHEMA = {
  // Document ID: auto-generated
  name: 'string', // e.g., "The Immunity Idol League"
  managerId: 'string', // Reference to the users collection (the user who created the league)
  description: 'string', // Optional league description
  isPrivate: 'boolean', // Whether the league is private or public
  maxTeams: 'number', // Maximum number of teams allowed
  isDeleted: 'boolean', // Soft-delete flag; hidden from UI when true
  deletedAt: 'timestamp', // When the league was soft-deleted
  createdAt: 'timestamp',
  updatedAt: 'timestamp'
};

/**
 * Predictions Collection Schema
 * Each document represents a single prediction submission
 */
export const PREDICTION_SCHEMA = {
  // Document ID: auto-generated
  leagueId: 'string', // Reference to the leagues collection
  seasonId: 'string', // Reference to the seasons collection
  teamId: 'string', // Reference to the teams collection
  week: 'number', // Week number
  predictions: {
    // Map with prediction type keys and contestantId values
    votedOut: ['string'], // Array of contestantIds for multiple predictions
    winner: 'string', // contestantId
    fireMakingLoser: 'string', // contestantId
    immunityWinner: 'string', // contestantId
    rewardWinner: 'string' // contestantId
  },
  submittedBy: 'string', // Reference to the users collection
  timestamp: 'timestamp',
  isLocked: 'boolean' // Whether predictions are locked (after deadline)
};

/**
 * Log Collection Schema
 * Each document represents a change to a league's rules
 */
export const LOG_SCHEMA = {
  // Document ID: auto-generated
  leagueId: 'string', // Reference to the leagues collection
  seasonId: 'string', // Reference to the seasons sub-collection within the league
  changeMade: 'string', // e.g., "Changed weekly survival points from 10 to 12"
  changedBy: 'string', // Reference to the users collection
  timestamp: 'timestamp',
  changeType: 'string', // 'scoring', 'rules', 'settings', etc.
  oldValue: 'any', // Previous value
  newValue: 'any' // New value
};

// ============================================================================
// SUB-COLLECTIONS
// ============================================================================

/**
 * Seasons Sub-collection within Leagues
 * Path: leagues/{leagueId}/seasons/{seasonId}
 */
/**
 * Draft status for a league-season.
 * Setup (draftRooms) can exist while status is still notStarted until the
 * commissioner starts the live draft.
 */
export const DRAFT_STATUS = {
  NOT_STARTED: 'notStarted',
  IN_PROGRESS: 'inProgress',
  COMPLETE: 'complete',
  FINALIZED: 'finalized'
};

export const DRAFT_ROOM_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'inProgress',
  COMPLETE: 'complete'
};

export const LEAGUE_SEASON_SCHEMA = {
  // Document ID: seasonId (reference to top-level seasons collection)
  seasonId: 'string', // Reference to the top-level seasons collection
  invitationCode: 'string', // A unique code for joining this specific season
  draftStatus: 'string', // DRAFT_STATUS: notStarted | inProgress | complete | finalized
  scoringRules: {
    // Map containing league-specific scoring values
    survivalPoints: 'number', // Points for each drafted contestant who survives a departure
    preMergePredictionPoints: 'number', // Points for correct predictions during pre-merge
    postMergePredictionPoints: 'number', // Points for correct predictions during post-merge
    winnerPoints: 'number' // Points for correctly predicting the ultimate winner
  },
  // Parallel draft rooms (one contestant pool per room; teams draft one each)
  draftRooms: [{
    id: 'number', // 1-based room id
    teamOrder: ['string'], // Team IDs in pick order
    currentPickIndex: 'number', // 0-based index into teamOrder
    status: 'string', // DRAFT_ROOM_STATUS
    pickHistory: [{
      teamId: 'string',
      contestantId: 'string',
      pickNumber: 'number',
      madeBy: 'string', // userId
      at: 'timestamp'
    }]
  }],
  draftOrder: ['string'], // Legacy: flat team IDs (kept for older seasons)
  currentDraftRound: 'number', // Legacy
  currentDraftPick: 'number', // Legacy
  draftStartTime: 'timestamp',
  draftEndTime: 'timestamp',
  draftFinalizedAt: 'timestamp', // Set when commissioner finalizes (locks draft)
  predictionDeadline: 'timestamp', // Weekly prediction deadline
  isActive: 'boolean'
};

/**
 * Teams Sub-collection within League Seasons
 * Path: leagues/{leagueId}/seasons/{seasonId}/teams/{teamId}
 */
export const TEAM_SCHEMA = {
  // Document ID: auto-generated
  name: 'string', // e.g., "Buff and Shine"
  players: ['string'], // Array of user IDs for all team members
  draftPicks: [{
    contestantId: 'string',
    draftRoomId: 'number', // Which draft room this pick belongs to
    pickNumber: 'number', // Pick slot within that room (1-based)
    // Legacy aliases (offline drafts / older UI): weekPicked === draftRoomId, draftId === pickNumber
    weekPicked: 'number',
    draftId: 'number'
  }],
  currentScore: 'number', // A running total
  weeklyScores: {
    // Map of week numbers to scores
    // Example: {'1': 15, '2': 20}
    type: 'map',
    value: 'number'
  },
  // Written when scores are recalculated; mirrors computeWeekScore output for UI
  weeklyScoreBreakdowns: {
    type: 'map',
    value: 'object' // week key -> { predictionPoints, survivalPoints, ... }
  },
  captain: 'string', // User ID of team captain
  createdAt: 'timestamp',
  updatedAt: 'timestamp'
};

// ============================================================================
// PERMISSIONS & ACCESS CONTROL
// ============================================================================

/**
 * User Roles and Permissions
 * These should match the role IDs in the roles collection
 */
export const USER_ROLES = {
  PLAYER: 'player',
  LEAGUE_MANAGER: 'league_manager',
  ADMIN: 'admin'
};

/**
 * Collection Access Permissions
 */
export const COLLECTION_PERMISSIONS = {
  users: {
    read: ['owner', 'admin'],
    write: ['owner', 'admin']
  },
  leagues: {
    read: ['authenticated'],
    write: ['league_manager', 'admin']
  },
  seasons: {
    read: ['authenticated'],
    write: ['admin']
  },
  contestants: {
    read: ['authenticated'],
    write: ['admin']
  },
  predictions: {
    read: ['teamMember', 'admin'], // Before results: own team only, after: all league members
    write: ['teamMember']
  },
  log: {
    read: ['leagueMember'],
    write: ['league_manager', 'admin']
  }
};

/**
 * Sub-collection Access Permissions
 */
export const SUB_COLLECTION_PERMISSIONS = {
  'leagues/{leagueId}/seasons': {
    read: ['leagueMember'],
    write: ['league_manager', 'admin']
  },
  'leagues/{leagueId}/seasons/{seasonId}/teams': {
    read: ['leagueMember'],
    write: ['teamMember', 'league_manager', 'admin']
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Validate user role permissions
 * @param {string} userRole - The user's role
 * @param {string} requiredRole - The required role for the action
 * @returns {boolean} - Whether the user has permission
 */
export function hasPermission(userRole, requiredRole) {
  const roleHierarchy = {
    [USER_ROLES.PLAYER]: 1,
    [USER_ROLES.LEAGUE_MANAGER]: 2,
    [USER_ROLES.ADMIN]: 3
  };
  
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

/**
 * Generate a unique invitation code for leagues
 * @returns {string} - A unique 8-character invitation code
 */
export function generateInvitationCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Determine if a week is pre-merge or post-merge
 * @param {number} weekNumber - The week number to check
 * @param {number} mergeWeek - The week number when the merge occurred (null if not merged yet)
 * @returns {string} - 'pre-merge' or 'post-merge'
 */
export function getWeekPhase(weekNumber, mergeWeek) {
  if (mergeWeek === null || mergeWeek === undefined) {
    return 'pre-merge'; // If no merge has occurred yet, all weeks are pre-merge
  }
  return weekNumber < mergeWeek ? 'pre-merge' : 'post-merge';
}

/**
 * Calculate team score for a specific week
 * @param {Object} predictions - Team's predictions for the week
 * @param {Object} results - Actual results for the week
 * @param {Object} scoringRules - League scoring rules
 * @param {number} weekNumber - The week number
 * @param {number} mergeWeek - The week number when the merge occurred
 * @returns {number} - Total score for the week
 */
export function calculateWeeklyScore(predictions, results, scoringRules, weekNumber, mergeWeek) {
  let score = 0;
  
  // Weekly survival points for each contestant still in the game
  if (predictions.draftPicks) {
    score += predictions.draftPicks.length * scoringRules.survivalPoints;
  }
  
  // Determine if this is pre-merge or post-merge for prediction points
  const weekPhase = getWeekPhase(weekNumber, mergeWeek);
  const predictionPoints = weekPhase === 'pre-merge' 
    ? scoringRules.preMergePredictionPoints 
    : scoringRules.postMergePredictionPoints;
  
  // Correct predictions
  if (predictions.votedOut && results.votedOut) {
    if (predictions.votedOut.includes(results.votedOut)) {
      score += predictionPoints;
    }
  }
  
  if (predictions.winner && results.winner && predictions.winner === results.winner) {
    score += scoringRules.winnerPoints;
  }
  
  return score;
}

export default {
  ROLE_SCHEMA,
  USER_SCHEMA,
  ELIMINATION_TYPES,
  SEASON_SCHEMA,
  CONTESTANT_SCHEMA,
  LEAGUE_SCHEMA,
  PREDICTION_SCHEMA,
  LOG_SCHEMA,
  LEAGUE_SEASON_SCHEMA,
  TEAM_SCHEMA,
  USER_ROLES,
  DRAFT_STATUS,
  DRAFT_ROOM_STATUS,
  COLLECTION_PERMISSIONS,
  SUB_COLLECTION_PERMISSIONS,
  hasPermission,
  generateInvitationCode,
  getWeekPhase,
  calculateWeeklyScore
};
